import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { setWorkerUrl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@maplibre/maplibre-gl-leaflet";
import { applyLanguage } from "../../utils/mapLabelLanguage";

// maplibre-gl >= 6 derives its worker URL from `import.meta.url`, which under
// CRA/webpack is a build-time `file://` string, so it must be set explicitly.
// The worker and the shared chunk it imports are copied to public/maplibre/ by
// scripts/copy-maplibre-worker.js (prestart/prebuild). Same-origin → MapLibre
// creates a module worker from this URL directly.
setWorkerUrl(`${process.env.PUBLIC_URL || ""}/maplibre/maplibre-gl-worker.mjs`);

const STYLE_URL =
    "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

interface VectorTileLayerProps {
    language?: string;
}

const VectorTileLayer: React.FC<VectorTileLayerProps> = ({
    language = "en",
}) => {
    const map = useMap();
    const layerRef = useRef<L.Layer | null>(null);

    useEffect(() => {
        let cancelled = false;

        fetch(STYLE_URL)
            .then((res) => res.json())
            .then((style) => {
                if (cancelled) return;

                const localizedStyle = applyLanguage(style, language);

                if (layerRef.current) {
                    map.removeLayer(layerRef.current);
                }

                const glLayer = (L as any).maplibreGL({
                    style: localizedStyle,
                    attributionControl: false,
                });

                glLayer.addTo(map);
                layerRef.current = glLayer;
            });

        return () => {
            cancelled = true;
            if (layerRef.current) {
                map.removeLayer(layerRef.current);
                layerRef.current = null;
            }
        };
    }, [map, language]);

    return null;
};

export default VectorTileLayer;
